/**
 * Responsibility: Implement effect chip behavior for the ui/helpers surface.
 * Scope: Owns ui/helpers concerns only; cross-surface orchestration is excluded.
 */
const FX_META_TAG = /\[(?:fx|icon|emoji)\s*:\s*([a-z0-9_\- ]{1,24})\]/i
const LEADING_EMOJI = /^(\p{Extended_Pictographic}(?:\uFE0F)?)(?:\s+|$)/u

function inferEmoji(text: string, dormant = false): string {
  if (!text) return dormant ? '💤' : '✨'
  if (/(poison|venom|toxic|blight|rot)/.test(text)) return '☠️'
  if (/(bleed|wound|burn|fire|scorch)/.test(text)) return '🔥'
  if (/(freeze|frost|ice|chill)/.test(text)) return '❄️'
  if (/(shock|stun|daze|sleep|paraly)/.test(text)) return '⚡'
  if (/(ward|shield|guard|barrier|armor|armour)/.test(text)) return '🛡️'
  if (/(regen|heal|mend|recovery|vigor)/.test(text)) return '💚'
  if (/(focus|insight|clarity|mind|spirit|will)/.test(text)) return '🧠'
  if (/(bless|inspire|valor|fortune|luck)/.test(text)) return '🌟'
  if (/(curse|hex|doom|fear|terror)/.test(text)) return '🕸️'
  if (/(stealth|shadow|invis|hidden|silent)/.test(text)) return '🕶️'
  return dormant ? '💤' : '✨'
}

function stripPresentationMarkers(label: string): string {
  return String(label ?? '')
    .replace(FX_META_TAG, '')
    .replace(LEADING_EMOJI, '')
    .replace(/\s+/g, ' ')
    .trim()
}

export function effectEmoji(label: string, dormant = false): string {
  const raw = String(label ?? '').trim()
  if (!raw) return dormant ? '💤' : '✨'
  const explicit = raw.match(LEADING_EMOJI)?.[1]
  if (explicit) return explicit
  const metaToken = raw.match(FX_META_TAG)?.[1]
  if (metaToken) return inferEmoji(metaToken.toLowerCase(), dormant)
  return inferEmoji(stripPresentationMarkers(raw).toLowerCase(), dormant)
}

export function effectChipText(label: string, dormant = false): string {
  const clean = stripPresentationMarkers(String(label ?? ''))
  if (!clean) return dormant ? '💤 Dormant effect' : '✨ Active effect'
  return `${effectEmoji(label, dormant)} ${clean}`
}
